/**
 * BB ID の付け直し(2026-10-12 発注者指示。D-205)。
 * 在籍スタッフの CSV(在籍・名前・出勤形態・架電内容・報酬区分・現BBID)から、
 *  - 名前の末尾の W(不用品用の名前)を外した「人」ごとにまとめる(山田太郎 と 山田太郎W は同じ人)
 *  - CSV に出てきた順に 1人1番号(001〜)を振り、その人の ID は全部同じ番号にする
 *  - 新BBID = 形態コード(D-71: 出勤形態＋報酬区分＋架電内容)＋番号3桁(例: 在宅×時給×給湯器の12番 = HHA012)
 *  - 旧BBID は消さずに「無効・新IDに置き換え済み」で残す(過去の成績・BlueBean 側の付け替え前の取込が同じ人に入るように)
 *  - 同じ人が別々のマスタ(山田太郎 と 山田太郎W)になっているときは、W の無い方に1人へまとめる
 * ここは計算だけ(Firebase を触らない)。書き込みは bbReissueRepo.ts。
 */
import { BUSINESS_CODES, PAY_TYPE_CODES, WORK_STYLE_CODES, type BbAccount, type BusinessType, type Operator, type PayType, type WorkStyle } from '@core/lib/types';

export const REISSUE_DIGITS = 3;

const WORK_STYLES: Record<string, WorkStyle> = { 管理者: 'kanri', 管理: 'kanri', 在宅: 'zaitaku', 出勤: 'shinagawa', 品川: 'shinagawa', タイミー: 'timee' };
const BUSINESSES: Record<string, BusinessType> = { 給湯器: 'kyutoki', 不用品: 'fuyohin', ホワイト: 'fuyohin' };
const PAY_TYPES: Record<string, PayType> = { 時給: 'hourly', 歩合: 'commission' };

export type ReissueRow = {
  /** CSV の行番号(1始まり) */
  line: number;
  /** CSV に書いてある名前(W 付きのまま) */
  csvName: string;
  /** W を外した名前(マスタの名前になる) */
  name: string;
  workStyle: WorkStyle;
  payType: PayType;
  business: BusinessType;
  /** 現BBID(大文字)。空なら旧IDなし */
  oldBbId: string;
};

export type ReissueParseError = { line: number; message: string };

/** 比べるための名前(空白を除き、全角英数を半角・大文字に) */
export function nameKey(s: string): string {
  return s
    .replace(/[\s　]/g, '')
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .toUpperCase();
}

/** 名前の末尾の W(半角・全角)を外す */
export function baseName(name: string): string {
  return name.trim().replace(/[WＷｗw]+$/, '').trim();
}

const normId = (s: string) => s.trim().toUpperCase();

/**
 * CSV を読む。1列目が「在籍」の行だけ使う(上の凡例の行・空行は読み飛ばす)。
 * 列: 在籍, 名前, 出勤形態(管理者/在宅/出勤/タイミー), 架電内容(給湯器/不用品), 報酬区分(時給/歩合), 現BBID
 */
export function parseReissueCsv(table: string[][]): { rows: ReissueRow[]; errors: ReissueParseError[] } {
  const rows: ReissueRow[] = [];
  const errors: ReissueParseError[] = [];
  table.forEach((cells, i) => {
    const line = i + 1;
    if ((cells[0] ?? '').trim() !== '在籍') return;
    const csvName = (cells[1] ?? '').trim();
    const ws = WORK_STYLES[(cells[2] ?? '').trim()];
    const biz = BUSINESSES[(cells[3] ?? '').trim()];
    const pay = PAY_TYPES[(cells[4] ?? '').trim()];
    const oldBbId = normId(cells[5] ?? '');
    const bad: string[] = [];
    if (!csvName) bad.push('名前が空です');
    if (!ws) bad.push(`出勤形態「${cells[2] ?? ''}」が分かりません(管理者・在宅・出勤・タイミー)`);
    if (!biz) bad.push(`架電内容「${cells[3] ?? ''}」が分かりません(給湯器・不用品)`);
    if (!pay) bad.push(`報酬区分「${cells[4] ?? ''}」が分かりません(時給・歩合)`);
    if (ws === 'timee' && pay === 'commission') bad.push('タイミーは時給だけです');
    if (bad.length > 0 || !ws || !biz || !pay) {
      errors.push({ line, message: bad.join(' ／ ') });
      return;
    }
    rows.push({ line, csvName, name: baseName(csvName), workStyle: ws, payType: pay, business: biz, oldBbId });
  });
  const seen = new Map<string, number>();
  for (const r of rows) {
    if (!r.oldBbId) continue;
    const first = seen.get(r.oldBbId);
    if (first !== undefined) errors.push({ line: r.line, message: `現BBID ${r.oldBbId} が ${first}行目と重なっています` });
    else seen.set(r.oldBbId, r.line);
  }
  return { rows, errors };
}

/** 新BBID = 形態コード(出勤形態＋報酬区分＋架電内容)＋番号(3桁) */
export function newBbIdOf(workStyle: WorkStyle, payType: PayType, business: BusinessType, no: number): string {
  return `${WORK_STYLE_CODES[workStyle]}${PAY_TYPE_CODES[payType]}${BUSINESS_CODES[business]}${String(no).padStart(REISSUE_DIGITS, '0')}`;
}

export type ReissueItem = {
  line: number;
  csvName: string;
  workStyle: WorkStyle;
  payType: PayType;
  business: BusinessType;
  oldBbId: string;
  newBbId: string;
  /** 旧BBID を持っているマスタ(無ければ null) */
  oldOpId: string | null;
  /** 新BBID がもうこの人に付いている(やり直し) */
  done: boolean;
};

export type ReissuePerson = {
  no: number;
  name: string;
  items: ReissueItem[];
  /** 残すマスタ(null なら新しく作る) */
  primary: Operator | null;
  /** primary にまとめるマスタ(W の付いた名前で別に登録されていた分など) */
  merge: Operator[];
  /** 名前だけで見つけた(BB ID では見つからなかった) */
  byName: boolean;
  /** 取り込めない理由(1つでもあれば全体を止める) */
  errors: string[];
  /** 注意(取り込みは止めない) */
  warnings: string[];
};

export type ReissuePlan = {
  persons: ReissuePerson[];
  /** CSV に無い在籍中のマスタ(何もしない。確認用) */
  notInCsv: Operator[];
  errorCount: number;
};

/**
 * 照合と番号付け。マスタは「旧BBID を持っている人」で探し、どの旧BBID でも見つからない人だけ名前で探す
 * (名前が W 付きで別に登録されていた分も、旧BBID でつながる)。
 */
export function planReissue(rows: ReissueRow[], operators: Operator[]): ReissuePlan {
  const ownerOf = new Map<string, Operator>();
  const accountOf = new Map<string, BbAccount>();
  for (const o of operators)
    for (const a of o.bb_accounts) {
      ownerOf.set(normId(a.bb_id), o);
      accountOf.set(normId(a.bb_id), a);
    }
  /** 新BBID がこの行の付け直しで作ったものか(旧IDから付け直した記録がある)。やり直しのときに同じ人として扱う */
  const reissuedFrom = (newId: string, oldId: string) => {
    const a = accountOf.get(newId);
    return !!a && !!oldId && normId(a.replaced_from ?? '') === oldId;
  };

  // CSV に出てきた順に1人1番号
  const groups = new Map<string, { name: string; rows: ReissueRow[] }>();
  for (const r of rows) {
    const k = nameKey(r.name);
    const g = groups.get(k) ?? { name: r.name, rows: [] };
    g.rows.push(r);
    groups.set(k, g);
  }

  const persons: ReissuePerson[] = [];
  let no = 0;
  for (const [key, g] of groups) {
    no += 1;
    const errors: string[] = [];
    const warnings: string[] = [];
    const found = new Map<string, Operator>();
    for (const r of g.rows) {
      const o = r.oldBbId ? ownerOf.get(r.oldBbId) : undefined;
      if (o) found.set(o.id, o);
    }
    // 前に付け直した新BBID を持っている人(やり直し)も同じ人として扱う。旧IDの記録が無い新IDは別の人なので見ない
    for (const r of g.rows) {
      const id = newBbIdOf(r.workStyle, r.payType, r.business, no);
      const o = ownerOf.get(id);
      if (o && reissuedFrom(id, r.oldBbId)) found.set(o.id, o);
    }
    let byName = false;
    if (found.size === 0) {
      const cands = operators.filter((o) => !o.merged_into && (nameKey(o.name) === key || nameKey(o.name) === `${key}W`));
      if (cands.length === 1) {
        found.set(cands[0]!.id, cands[0]!);
        byName = true;
      } else if (cands.length > 1) {
        errors.push(`BB ID で見つからず、同じ名前のマスタが ${cands.length}件あるため決められません`);
      }
    }
    const ops = [...found.values()];
    // 残すのは W の付かない名前のマスタ。無ければ管理番号の若い方
    const primary =
      ops.find((o) => nameKey(o.name) === key) ?? [...ops].sort((a, b) => a.op_code.localeCompare(b.op_code))[0] ?? null;
    const merge = ops.filter((o) => o.id !== primary?.id);

    const items: ReissueItem[] = g.rows.map((r) => {
      const newBbId = newBbIdOf(r.workStyle, r.payType, r.business, no);
      const newOwner = ownerOf.get(newBbId);
      if (newOwner && !found.has(newOwner.id)) errors.push(`新BBID ${newBbId} は別の人(${newOwner.name})が使っています`);
      const oldOwner = r.oldBbId ? (ownerOf.get(r.oldBbId) ?? null) : null;
      if (r.oldBbId && !oldOwner) warnings.push(`旧BBID ${r.oldBbId} はマスタに登録されていません(旧IDとして足します)`);
      return {
        line: r.line,
        csvName: r.csvName,
        workStyle: r.workStyle,
        payType: r.payType,
        business: r.business,
        oldBbId: r.oldBbId,
        newBbId,
        oldOpId: oldOwner?.id ?? null,
        done: !!newOwner && found.has(newOwner.id),
      };
    });
    const dupNew = items.map((i) => i.newBbId).filter((id, i, a) => a.indexOf(id) !== i);
    for (const id of new Set(dupNew)) errors.push(`同じ人に同じ種別が2行あり、新BBID ${id} が重なります`);
    if (!primary && !byName && errors.length === 0) warnings.push('マスタに見つからないため、新しく登録します');
    if (primary && primary.status !== 'active') warnings.push(`マスタは「在籍」ではありません(${primary.status})。在籍に戻すときは編集から`);
    for (const m of merge) if (m.status !== 'active') warnings.push(`まとめる元「${m.name}」は在籍ではありません(${m.status})`);
    persons.push({ no, name: g.name, items, primary, merge, byName, errors, warnings });
  }

  // 1つのマスタが2人に照合されたら止める
  const usedBy = new Map<string, string>();
  for (const p of persons) {
    for (const o of [p.primary, ...p.merge]) {
      if (!o) continue;
      const other = usedBy.get(o.id);
      if (other && other !== p.name) p.errors.push(`マスタ「${o.name}」が「${other}」にも照合されています`);
      else usedBy.set(o.id, p.name);
    }
  }

  const notInCsv = operators.filter((o) => o.status === 'active' && !o.merged_into && !usedBy.has(o.id));
  return { persons, notInCsv, errorCount: persons.reduce((n, p) => n + p.errors.length, 0) };
}

/**
 * 付け直したあとの BB ID の並び(先頭が新ID)。
 *  - 新ID: 旧IDの架電会社・時給(単価)・改定履歴を引き継ぐ。旧IDがマスタに無ければ時給は未設定(仮登録と同じ)
 *  - 旧ID: 無効にして replaced_by に新IDを書く(取込は無効のIDでも本人に結び付く)
 *  - CSV に無い BB ID(まとめる元にあった分も)はそのまま残す
 */
export function reissuedAccounts(person: ReissuePerson): BbAccount[] {
  const all = [person.primary, ...person.merge].flatMap((o) => o?.bb_accounts ?? []);
  const byId = new Map(all.map((a) => [normId(a.bb_id), a]));
  const created: BbAccount[] = [];
  const handled = new Set<string>();
  const ext = String(person.no).padStart(REISSUE_DIGITS, '0');
  for (const it of person.items) {
    const existingNew = byId.get(it.newBbId);
    const old = it.oldBbId ? byId.get(it.oldBbId) : undefined;
    if (existingNew) {
      created.push(existingNew);
      handled.add(it.newBbId);
    } else {
      created.push({
        work_style: it.workStyle,
        pay_type: it.payType,
        bb_id: it.newBbId,
        ext,
        bb_internal_id: null,
        company_id: old?.company_id ?? null,
        business: it.business,
        active: true,
        current_wage: old?.current_wage ?? 0,
        ...(old ? (old.wage_pending ? { wage_pending: true } : {}) : { wage_pending: true }),
        wage_history: old?.wage_history ?? [],
        replaced_from: it.oldBbId || null,
      });
    }
    if (it.oldBbId) {
      handled.add(it.oldBbId);
      created.push(
        old
          ? { ...old, active: false, replaced_by: it.newBbId }
          : {
              work_style: it.workStyle,
              pay_type: it.payType,
              bb_id: it.oldBbId,
              ext: null,
              bb_internal_id: null,
              company_id: null,
              business: it.business,
              active: false,
              current_wage: 0,
              wage_pending: true,
              wage_history: [],
              replaced_by: it.newBbId,
            },
      );
    }
  }
  const rest = all.filter((a) => !handled.has(normId(a.bb_id)) && !created.includes(a));
  // 新IDを先に(一覧の出勤形態・報酬区分は先頭のIDで決まる)、次に旧ID、最後にCSVに無いID
  const seen = new Set<string>();
  return [...created.filter((a) => a.active), ...created.filter((a) => !a.active), ...rest].filter((a) => {
    const k = normId(a.bb_id);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * まとめる元のマスタ(from)の ID を、残すマスタ(to)の ID に書き換えた値を返す(変わらなければ null)。
 *  - `op_id` という名前の項目の値
 *  - キーがマスタの ID になっている対応表(納品件数・目標など)のキー。両方にキーがあるときは書き換えず conflict を返す
 * 配列・入れ子もたどる。マスタの ID は Firestore の自動ID(20文字)なので、他の値と取り違えない。
 */
export function rewriteOpRefs(value: unknown, map: Map<string, string>): { value: unknown; changed: boolean; conflict: boolean } {
  let changed = false;
  let conflict = false;
  const walk = (v: unknown, key: string | null): unknown => {
    if (typeof v === 'string') {
      if (key === 'op_id' && map.has(v)) {
        changed = true;
        return map.get(v);
      }
      return v;
    }
    if (Array.isArray(v)) return v.map((x) => walk(x, null));
    if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
      const out: Record<string, unknown> = {};
      const entries = Object.entries(v as Record<string, unknown>);
      for (const [k, x] of entries) {
        const to = map.get(k);
        if (to !== undefined) {
          if (Object.prototype.hasOwnProperty.call(v, to)) {
            conflict = true;
            out[k] = walk(x, k);
          } else {
            changed = true;
            out[to] = walk(x, to);
          }
        } else {
          out[k] = walk(x, k);
        }
      }
      return out;
    }
    return v;
  };
  const next = walk(value, null);
  return { value: next, changed, conflict };
}
